import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { portalMessageQueryKeys } from "@/hooks/portalMessages/portalMessageQueryKeys";
import type {
  PortalConversationSummary,
  PortalInboxResponse,
  PortalMessageChangeConversation,
  PortalMessageChangedEvent,
  PortalMessage,
  PortalMessageAvatar,
  PortalThreadMember,
  PortalThreadResponse,
} from "@/types/portalMessages";

const EMPTY_AVATAR: PortalMessageAvatar = {
  url: null,
  publicId: null,
};

type PortalThreadCache =
  | PortalThreadResponse
  | InfiniteData<PortalThreadResponse, string | null>;

const appliedMessageRevisions = new WeakMap<QueryClient, Map<string, number>>();

function isInfiniteThreadCache(
  thread: PortalThreadCache,
): thread is InfiniteData<PortalThreadResponse, string | null> {
  return "pages" in thread && Array.isArray(thread.pages);
}

function upsertNewestMessage(
  messages: PortalMessage[],
  message: PortalMessage,
): PortalMessage[] {
  const existingIndex = messages.findIndex(
    (existing) =>
      existing._id === message._id ||
      (message.clientMessageId &&
        existing.clientMessageId === message.clientMessageId),
  );

  if (existingIndex >= 0) {
    const nextMessages = [...messages];
    nextMessages[existingIndex] = {
      ...nextMessages[existingIndex],
      ...message,
      isOptimistic: false,
    };
    return nextMessages;
  }

  return [...messages, message];
}

function removeMessageByClientId(
  messages: PortalMessage[],
  clientMessageId: string,
): PortalMessage[] {
  return messages.filter((message) => message.clientMessageId !== clientMessageId);
}

function markMessageDelivered(
  messages: PortalMessage[],
  messageId: string,
  clientMessageId?: string | null,
): PortalMessage[] {
  return messages.map((message) => {
    const matches = matchesDeliveredMessage(message, messageId, clientMessageId);

    return matches
      ? {
          ...message,
          _id: messageId,
          clientMessageId: clientMessageId ?? message.clientMessageId,
          isOptimistic: Boolean(message.isOptimistic && !message.senderStaffId),
        }
      : message;
  });
}

function matchesDeliveredMessage(
  message: PortalMessage,
  messageId: string,
  clientMessageId?: string | null,
) {
  return message._id === messageId ||
    Boolean(clientMessageId && message.clientMessageId === clientMessageId);
}

function matchesSearchFilter(name: string, search?: string) {
  const normalizedSearch = search?.trim().toLowerCase();

  if (!normalizedSearch) {
    return true;
  }

  return name.toLowerCase().includes(normalizedSearch);
}

function sortConversationsByLatest(
  conversations: PortalConversationSummary[],
): PortalConversationSummary[] {
  return [...conversations].sort(
    (first, second) =>
      new Date(second.lastMessageAt ?? 0).getTime() -
      new Date(first.lastMessageAt ?? 0).getTime(),
  );
}

function updateInboxConversation(
  inbox: PortalInboxResponse,
  matcher: (conversation: PortalConversationSummary) => boolean,
  updater: (conversation: PortalConversationSummary) => PortalConversationSummary,
) {
  const index = inbox.conversations.findIndex(matcher);

  if (index === -1) {
    return inbox;
  }

  const nextConversations = [...inbox.conversations];
  nextConversations[index] = updater(nextConversations[index]);

  return {
    ...inbox,
    conversations: sortConversationsByLatest(nextConversations),
  };
}

export function getCachedThreadMember(
  queryClient: QueryClient,
  memberId: string,
): PortalThreadMember | null {
  const threadEntries = queryClient.getQueriesData<PortalThreadCache>({
    queryKey: portalMessageQueryKeys.thread(memberId),
  });

  for (const [, thread] of threadEntries) {
    if (!thread) {
      continue;
    }

    if (isInfiniteThreadCache(thread)) {
      const member = thread.pages.find((page) => page.member)?.member;

      if (member) {
        return member;
      }

      continue;
    }

    if (thread.member) {
      return thread.member;
    }
  }

  return null;
}

export function getCachedConversation(
  queryClient: QueryClient,
  memberId: string,
): PortalConversationSummary | null {
  const conversationEntries = queryClient.getQueriesData<PortalInboxResponse>({
    queryKey: portalMessageQueryKeys.conversations,
  });

  for (const [, inbox] of conversationEntries) {
    const conversation = inbox?.conversations.find(
      (item) => item.memberId === memberId,
    );

    if (conversation) {
      return conversation;
    }
  }

  return null;
}

export function upsertThreadFromMessage(
  queryClient: QueryClient,
  memberId: string,
  message: PortalMessage,
  member?: PortalThreadMember | null,
) {
  const threadEntries = queryClient.getQueriesData<PortalThreadCache>({
    queryKey: portalMessageQueryKeys.thread(memberId),
  });

  if (threadEntries.length === 0 && member) {
    queryClient.setQueryData(
      portalMessageQueryKeys.threadCursor(memberId, { limit: 20 }),
      {
        pages: [
          {
            messages: [message],
            paging: {
              hasMore: false,
              nextCursor: null,
            },
            conversationId: message.conversationId,
            member,
          },
        ],
        pageParams: [null],
      } satisfies InfiniteData<PortalThreadResponse, string | null>,
    );
    return;
  }

  for (const [queryKey, thread] of threadEntries) {
    if (!thread) {
      continue;
    }

    if (isInfiniteThreadCache(thread)) {
      const firstPage = thread.pages[0] ?? {
        messages: [],
        paging: {
          hasMore: false,
          nextCursor: null,
        },
        conversationId: message.conversationId,
        member: member!,
      };
      const nextPages = [...thread.pages];
      nextPages[0] = {
        ...firstPage,
        conversationId: firstPage.conversationId ?? message.conversationId,
        member: member ?? firstPage.member,
        messages: upsertNewestMessage(firstPage.messages, message),
      };

      queryClient.setQueryData<PortalThreadCache>(queryKey, {
        ...thread,
        pages: nextPages,
      });
      continue;
    }

    queryClient.setQueryData<PortalThreadResponse>(queryKey, {
      ...thread,
      conversationId: thread.conversationId ?? message.conversationId,
      member: member ?? thread.member,
      messages: upsertNewestMessage(thread.messages, message),
    });
  }
}

export function removeOptimisticThreadMessage(
  queryClient: QueryClient,
  memberId: string,
  clientMessageId: string,
) {
  const threadEntries = queryClient.getQueriesData<PortalThreadCache>({
    queryKey: portalMessageQueryKeys.thread(memberId),
  });

  for (const [queryKey, thread] of threadEntries) {
    if (!thread) {
      continue;
    }

    if (isInfiniteThreadCache(thread)) {
      queryClient.setQueryData<PortalThreadCache>(queryKey, {
        ...thread,
        pages: thread.pages.map((page) => ({
          ...page,
          messages: removeMessageByClientId(page.messages, clientMessageId),
        })),
      });
      continue;
    }

    queryClient.setQueryData<PortalThreadResponse>(queryKey, {
      ...thread,
      messages: removeMessageByClientId(thread.messages, clientMessageId),
    });
  }
}

export function upsertInboxConversation(
  queryClient: QueryClient,
  conversation: PortalConversationSummary,
) {
  const inboxEntries = queryClient.getQueriesData<PortalInboxResponse>({
    queryKey: portalMessageQueryKeys.conversations,
  });

  for (const [queryKey, inbox] of inboxEntries) {
    if (!inbox) {
      continue;
    }

    const [, , params] = queryKey as ReturnType<
      typeof portalMessageQueryKeys.conversationList
    >;

    const nextConversation = {
      ...conversation,
      avatar: conversation.avatar ?? EMPTY_AVATAR,
    };

    const existingIndex = inbox.conversations.findIndex(
      (item) => item.memberId === conversation.memberId,
    );

    if (existingIndex >= 0) {
      const nextConversations = [...inbox.conversations];
      nextConversations[existingIndex] = {
        ...nextConversations[existingIndex],
        ...nextConversation,
        subscriptionStatus:
          nextConversation.subscriptionStatus ??
          nextConversations[existingIndex].subscriptionStatus,
      };

      queryClient.setQueryData<PortalInboxResponse>(queryKey, {
        ...inbox,
        conversations: sortConversationsByLatest(nextConversations),
      });
      continue;
    }

    if (!matchesSearchFilter(nextConversation.name, params?.search)) {
      continue;
    }

    queryClient.setQueryData<PortalInboxResponse>(queryKey, {
      ...inbox,
      totalCount: inbox.totalCount + 1,
      conversations: sortConversationsByLatest([
        nextConversation,
        ...inbox.conversations,
      ]),
    });
  }
}

function mapCachedMessages(
  messages: PortalMessage[],
  changedEvent: PortalMessageChangedEvent,
) {
  let didChange = false;
  const messagesNext = messages.map((message) => {
    if (message._id === changedEvent.message._id) {
      didChange = true;
      return {
        ...message,
        ...changedEvent.message,
        revision: changedEvent.revision,
        isOptimistic: false,
      };
    }

    if (
      changedEvent.replyPreviewUpdates &&
      changedEvent.replyPreviewUpdates.targetMessageId === message.replyToMessageId &&
      message.replyPreview
    ) {
      didChange = true;
      return {
        ...message,
        replyPreview: {
          ...message.replyPreview,
          text: changedEvent.replyPreviewUpdates.text,
        },
      };
    }

    return message;
  });

  return { messages: didChange ? messagesNext : messages, didChange };
}

function applyConversationSummary(
  queryClient: QueryClient,
  message: PortalMessage,
  conversation?: PortalMessageChangeConversation | null,
  previousText?: string,
) {
  const conversationId =
    conversation?.conversationId ?? conversation?._id ?? message.conversationId;
  const summaryText = conversation?.lastMessageText ?? conversation?.lastMessage;
  const summaryAt = conversation?.lastMessageAt;
  const summaryTime = summaryAt ? Date.parse(summaryAt) : NaN;
  let foundConversation = false;
  let needsRefetch = false;
  const inboxEntries = queryClient.getQueriesData<PortalInboxResponse>({
    queryKey: portalMessageQueryKeys.conversations,
  });

  for (const [queryKey, inbox] of inboxEntries) {
    if (!inbox) continue;
    const current = inbox.conversations.find((item) => item.memberId === message.memberId);
    if (!current) continue;
    foundConversation = true;

    if (!Number.isFinite(summaryTime)) {
      needsRefetch = true;
      continue;
    }

    const currentTime = current.lastMessageAt ? Date.parse(current.lastMessageAt) : -Infinity;
    if (!Number.isFinite(currentTime) && currentTime !== -Infinity) {
      needsRefetch = true;
      continue;
    }
    if (summaryTime < currentTime) continue;

    const lastMessage = summaryText ??
      (summaryAt === message.createdAt ? message.text : current.lastMessage);
    if (
      summaryTime === currentTime &&
      lastMessage !== current.lastMessage &&
      (summaryAt !== message.createdAt || previousText !== current.lastMessage)
    ) {
      // A timestamp tie does not identify which message supplied the cached preview.
      needsRefetch = true;
      continue;
    }

    queryClient.setQueryData<PortalInboxResponse>(
      queryKey,
      updateInboxConversation(
        inbox,
        (item) => item.memberId === message.memberId,
        (item) => ({
          ...item,
          conversationId,
          lastMessage,
          lastMessageAt: summaryAt ?? item.lastMessageAt,
        }),
      ),
    );
  }

  return foundConversation && !needsRefetch;
}

export function applyPortalMessageChangedEvent(
  queryClient: QueryClient,
  changedEvent: PortalMessageChangedEvent,
) {
  const revision = changedEvent.revision ?? changedEvent.message.revision ?? 0;
  let revisions = appliedMessageRevisions.get(queryClient);
  if (!revisions) {
    revisions = new Map<string, number>();
    appliedMessageRevisions.set(queryClient, revisions);
  }

  let latestCachedRevision = revisions.get(changedEvent.message._id) ?? 0;
  let previousText: string | undefined;
  const threadEntries = queryClient.getQueriesData<PortalThreadCache>({
    queryKey: portalMessageQueryKeys.threads,
  });
  for (const [queryKey, thread] of threadEntries) {
    if (queryKey[2] !== changedEvent.message.memberId || !thread) continue;
    const cachedMessages = isInfiniteThreadCache(thread)
      ? thread.pages.flatMap((page) => page.messages)
      : thread.messages;
    const cachedTarget = cachedMessages.find(
      (message) => message._id === changedEvent.message._id,
    );
    previousText ??= cachedTarget?.text;
    latestCachedRevision = Math.max(latestCachedRevision, cachedTarget?.revision ?? 0);
  }

  if (revision < latestCachedRevision) {
    return { applied: false, threadUpdated: false, conversationUpdated: false };
  }
  revisions.set(changedEvent.message._id, revision);

  let threadUpdated = false;
  for (const [queryKey, thread] of threadEntries) {
    if (queryKey[2] !== changedEvent.message.memberId || !thread) continue;

    if (isInfiniteThreadCache(thread)) {
      let changed = false;
      const pages = thread.pages.map((page) => {
        const result = mapCachedMessages(page.messages, changedEvent);
        changed ||= result.didChange;
        return result.didChange ? { ...page, messages: result.messages } : page;
      });
      if (changed) {
        threadUpdated = true;
        queryClient.setQueryData<PortalThreadCache>(queryKey, { ...thread, pages });
      }
      continue;
    }

    const result = mapCachedMessages(thread.messages, changedEvent);
    if (result.didChange) {
      threadUpdated = true;
      queryClient.setQueryData<PortalThreadResponse>(queryKey, {
        ...thread,
        messages: result.messages,
      });
    }
  }

  const conversationUpdated = applyConversationSummary(
    queryClient,
    { ...changedEvent.message, revision },
    changedEvent.conversation,
    previousText,
  );
  return { applied: true, threadUpdated, conversationUpdated };
}

export function applyIncomingMessageToInbox(
  queryClient: QueryClient,
  message: PortalMessage,
) {
  const inboxEntries = queryClient.getQueriesData<PortalInboxResponse>({
    queryKey: portalMessageQueryKeys.conversations,
  });

  let conversationFound = false;

  for (const [queryKey, inbox] of inboxEntries) {
    if (!inbox) {
      continue;
    }

    const nextInbox = updateInboxConversation(
      inbox,
      (conversation) => conversation.memberId === message.memberId,
      (conversation) => ({
        ...conversation,
        conversationId: message.conversationId,
        lastMessage: message.text,
        lastMessageAt: message.createdAt,
        unreadCount:
          message.senderType === "member" ? conversation.unreadCount + 1 : 0,
      }),
    );

    if (nextInbox !== inbox) {
      conversationFound = true;
      queryClient.setQueryData<PortalInboxResponse>(queryKey, nextInbox);
    }
  }

  return conversationFound;
}

export function clearInboxUnreadCount(
  queryClient: QueryClient,
  memberId: string,
) {
  const inboxEntries = queryClient.getQueriesData<PortalInboxResponse>({
    queryKey: portalMessageQueryKeys.conversations,
  });

  for (const [queryKey, inbox] of inboxEntries) {
    if (!inbox) {
      continue;
    }

    const nextInbox = updateInboxConversation(
      inbox,
      (conversation) => conversation.memberId === memberId,
      (conversation) => ({
        ...conversation,
        unreadCount: 0,
      }),
    );

    if (nextInbox !== inbox) {
      queryClient.setQueryData<PortalInboxResponse>(queryKey, nextInbox);
    }
  }
}

export function clearInboxUnreadCountByConversationId(
  queryClient: QueryClient,
  conversationId: string,
) {
  const inboxEntries = queryClient.getQueriesData<PortalInboxResponse>({
    queryKey: portalMessageQueryKeys.conversations,
  });

  for (const [queryKey, inbox] of inboxEntries) {
    if (!inbox) {
      continue;
    }

    const nextInbox = updateInboxConversation(
      inbox,
      (conversation) => conversation.conversationId === conversationId,
      (conversation) => ({
        ...conversation,
        unreadCount: 0,
      }),
    );

    if (nextInbox !== inbox) {
      queryClient.setQueryData<PortalInboxResponse>(queryKey, nextInbox);
    }
  }
}

export function applyMessageDeliveryToPortalCaches(
  queryClient: QueryClient,
  conversationId: string,
  messageId: string,
  clientMessageId?: string | null,
) {
  const membersNeedingRefresh = new Set<string>();
  const threadEntries = queryClient.getQueriesData<PortalThreadCache>({
    queryKey: portalMessageQueryKeys.threads,
  });

  for (const [queryKey, thread] of threadEntries) {
    if (!thread) {
      continue;
    }

    if (isInfiniteThreadCache(thread)) {
      const belongsToThread = thread.pages.some(
        (page) => page.conversationId === conversationId,
      );

      if (!belongsToThread) {
        continue;
      }

      if (thread.pages.some((page) => page.messages.some(
        (message) => matchesDeliveredMessage(message, messageId, clientMessageId) &&
          message.isOptimistic && !message.senderStaffId,
      ))) {
        membersNeedingRefresh.add(queryKey[2] as string);
      }

      queryClient.setQueryData<PortalThreadCache>(queryKey, {
        ...thread,
        pages: thread.pages.map((page) => ({
          ...page,
          messages: markMessageDelivered(
            page.messages,
            messageId,
            clientMessageId,
          ),
        })),
      });
      continue;
    }

    if (thread.conversationId !== conversationId) {
      continue;
    }

    if (thread.messages.some(
      (message) => matchesDeliveredMessage(message, messageId, clientMessageId) &&
        message.isOptimistic && !message.senderStaffId,
    )) {
      membersNeedingRefresh.add(queryKey[2] as string);
    }

    queryClient.setQueryData<PortalThreadResponse>(queryKey, {
      ...thread,
      messages: markMessageDelivered(
        thread.messages,
        messageId,
        clientMessageId,
      ),
    });
  }

  return [...membersNeedingRefresh];
}

export function applySocketMessageToPortalCaches(
  queryClient: QueryClient,
  message: PortalMessage,
) {
  const cachedThreadMember = getCachedThreadMember(queryClient, message.memberId);
  upsertThreadFromMessage(
    queryClient,
    message.memberId,
    message,
    cachedThreadMember,
  );

  return applyIncomingMessageToInbox(queryClient, message);
}

export function clearSocketReadStateFromPortalCaches(
  queryClient: QueryClient,
  memberId: string,
) {
  clearInboxUnreadCount(queryClient, memberId);
}
